(() => {
  if (window.__parentlyCollectionCardImageNormalizer) return;
  window.__parentlyCollectionCardImageNormalizer = true;

  const SAMPLE_SIZE = 96;
  const TARGET_CONTENT_HEIGHT = 0.86;
  const MIN_SCALE = 0.82;
  const MAX_SCALE = 1.28;
  const cardsSelector = '.product-card-wrapper [data-card-primary-image]';

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  function getSampleSources(source) {
    const sources = [source];

    try {
      const parsed = new URL(source, document.baseURI);
      if (parsed.origin === window.location.origin) return sources;

      let storePath = parsed.pathname;
      const productPath = storePath.indexOf('/products/');
      const filePath = storePath.indexOf('/files/');

      if (productPath >= 0) {
        storePath = `/cdn/shop/products/${storePath.slice(productPath + '/products/'.length)}`;
      } else if (filePath >= 0) {
        storePath = `/cdn/shop/files/${storePath.slice(filePath + '/files/'.length)}`;
      }

      const storeSource = `${window.location.origin}${storePath}${parsed.search}`;
      if (storeSource !== source) sources.unshift(storeSource);
    } catch (error) {
      // Keep the original source as the fallback for unusual image URLs.
    }

    return sources;
  }

  function getContentBounds(image) {
    const source = image.currentSrc || image.src;
    if (!source || !image.naturalWidth || !image.naturalHeight) return null;

    const sample = new Image();
    sample.crossOrigin = 'anonymous';
    const sampleSources = getSampleSources(source);

    return new Promise((resolve) => {
      let sourceIndex = 0;
      let settled = false;

      const finish = (result) => {
        if (settled) return;
        settled = true;
        resolve(result);
      };

      const tryNextSource = () => {
        if (sourceIndex >= sampleSources.length) {
          finish(null);
          return;
        }

        sample.src = sampleSources[sourceIndex];
        sourceIndex += 1;
      };

      sample.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = SAMPLE_SIZE;
        canvas.height = SAMPLE_SIZE;

        const context = canvas.getContext('2d', { willReadFrequently: true });
        if (!context) {
          finish(null);
          return;
        }

        try {
          context.drawImage(sample, 0, 0, SAMPLE_SIZE, SAMPLE_SIZE);
          const pixels = context.getImageData(0, 0, SAMPLE_SIZE, SAMPLE_SIZE).data;
          const cornerSamples = [
            [2, 2],
            [SAMPLE_SIZE - 3, 2],
            [2, SAMPLE_SIZE - 3],
            [SAMPLE_SIZE - 3, SAMPLE_SIZE - 3],
          ];
          const background = cornerSamples.reduce(
            (sum, [x, y]) => {
              const index = (y * SAMPLE_SIZE + x) * 4;
              sum[0] += pixels[index];
              sum[1] += pixels[index + 1];
              sum[2] += pixels[index + 2];
              return sum;
            },
            [0, 0, 0]
          ).map((channel) => channel / cornerSamples.length);

          let minX = SAMPLE_SIZE;
          let minY = SAMPLE_SIZE;
          let maxX = -1;
          let maxY = -1;
          let foregroundPixels = 0;

          for (let y = 0; y < SAMPLE_SIZE; y += 1) {
            for (let x = 0; x < SAMPLE_SIZE; x += 1) {
              const index = (y * SAMPLE_SIZE + x) * 4;
              const alpha = pixels[index + 3];
              if (alpha < 20) continue;

              const red = pixels[index];
              const green = pixels[index + 1];
              const blue = pixels[index + 2];
              const difference = Math.sqrt(
                (red - background[0]) ** 2
                  + (green - background[1]) ** 2
                  + (blue - background[2]) ** 2
              );
              const isNearWhite = red > 246 && green > 246 && blue > 246;

              if (isNearWhite || difference < 24) continue;

              foregroundPixels += 1;
              minX = Math.min(minX, x);
              minY = Math.min(minY, y);
              maxX = Math.max(maxX, x);
              maxY = Math.max(maxY, y);
            }
          }

          const width = maxX - minX + 1;
          const height = maxY - minY + 1;
          if (foregroundPixels < 40 || width < 12 || height < 18) {
            finish(null);
            return;
          }

          finish({
            left: minX / SAMPLE_SIZE,
            top: minY / SAMPLE_SIZE,
            width: width / SAMPLE_SIZE,
            height: height / SAMPLE_SIZE,
          });
        } catch (error) {
          // Try the CDN source if the same-origin route was unavailable.
          tryNextSource();
        }
      };

      sample.onerror = tryNextSource;
      tryNextSource();
    });
  }

  async function normaliseImage(image) {
    if (!image || !image.naturalWidth || !image.naturalHeight) return;

    const source = image.currentSrc || image.src;
    if (!source || image.dataset.normalisedSource === source) return;
    image.dataset.normalisedSource = source;

    const bounds = await getContentBounds(image);
    if (!bounds || (image.currentSrc || image.src) !== source) return;

    const box = image.getBoundingClientRect();
    if (box.width < 20 || box.height < 20) return;

    const sourceRatio = image.naturalWidth / image.naturalHeight;
    const fitScale = Math.min(box.width / image.naturalWidth, box.height / image.naturalHeight);
    const renderedWidth = image.naturalWidth * fitScale;
    const renderedHeight = image.naturalHeight * fitScale;
    const offsetX = (box.width - renderedWidth) / 2;
    const offsetY = (box.height - renderedHeight) / 2;
    const contentHeight = bounds.height * renderedHeight;
    const contentScale = clamp((box.height * TARGET_CONTENT_HEIGHT) / contentHeight, MIN_SCALE, MAX_SCALE);

    const contentCenterX = offsetX + (bounds.left + bounds.width / 2) * renderedWidth;
    const contentCenterY = offsetY + (bounds.top + bounds.height / 2) * renderedHeight;
    const scaledCenterX = box.width / 2 + (contentCenterX - box.width / 2) * contentScale;
    const scaledCenterY = box.height / 2 + (contentCenterY - box.height / 2) * contentScale;
    const translateX = box.width / 2 - scaledCenterX;
    const translateY = box.height / 2 - scaledCenterY;

    image.style.transform = `translate3d(${translateX.toFixed(2)}px, ${translateY.toFixed(2)}px, 0) scale(${contentScale.toFixed(4)})`;
    image.dataset.normalisedRatio = sourceRatio.toFixed(4);
  }

  function watchImage(image) {
    if (image.dataset.normaliserAttached === 'true') return;
    image.dataset.normaliserAttached = 'true';

    const schedule = () => {
      delete image.dataset.normalisedSource;
      window.requestAnimationFrame(() => normaliseImage(image));
    };

    image.addEventListener('load', schedule, { passive: true });
    image.addEventListener('error', () => {
      image.style.removeProperty('transform');
      delete image.dataset.normalisedSource;
    }, { passive: true });

    if (image.complete) schedule();
  }

  function scan(root = document) {
    root.querySelectorAll(cardsSelector).forEach(watchImage);
  }

  function initialise() {
    scan();

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) scan(node);
        });

        if (
          mutation.type === 'attributes'
          && (mutation.attributeName === 'src' || mutation.attributeName === 'srcset')
          && mutation.target.matches?.(cardsSelector)
        ) {
          watchImage(mutation.target);
          delete mutation.target.dataset.normalisedSource;
          window.requestAnimationFrame(() => normaliseImage(mutation.target));
        }
      });
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['src', 'srcset'],
      childList: true,
      subtree: true,
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialise, { once: true });
  } else {
    initialise();
  }
})();
